import { RadioGroupProps, FormControlLabelProps } from '@mui/material';
import { useCallback } from 'react';
import RadioCard, { RadioCardProps } from './RadioCard';
import * as S from './styles';

export interface RadioCardGroupProps<TValue = string>
  extends Omit<RadioGroupProps, 'value' | 'onChange'> {
  options: RadioCardProps<TValue>[];
  value: TValue;
  gap?: string;
  labelPlacement?: FormControlLabelProps['labelPlacement'];
  onChange: (value: TValue) => void;
}

const RadioCardGroup = <TValue,>({
  options,
  value,
  gap,
  labelPlacement = 'end',
  onChange,
  ...radioGroupProps
}: RadioCardGroupProps<TValue>) => {
  const handleChange = useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, newValue: string): void => {
      // Find the option with the matching stringified value to get the original typed value.
      const selectedOption = options.find(
        (opt) => String(opt.value) === newValue,
      );

      if (selectedOption) {
        onChange(selectedOption.value);
      }
    },
    [onChange, options],
  );

  return (
    <S.RadioCardGroup
      {...radioGroupProps}
      $gap={gap}
      value={String(value)}
      onChange={handleChange}
    >
      {options.map((option) => (
        <RadioCard
          key={String(option.value)}
          value={option.value}
          label={option.label}
          description={option.description}
          labelPlacement={labelPlacement}
          renderIndicator={option.renderIndicator}
          isChecked={value === option.value}
          onClick={onChange}
        >
          {option.children}
        </RadioCard>
      ))}
    </S.RadioCardGroup>
  );
};

export default RadioCardGroup;
